import { createFileRoute, useParams } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useState } from "react";
import { Loader2, Sparkles } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { aiInterpretPaste } from "@/lib/ai.functions";
import { commitManualPaste } from "@/lib/imports.functions";
import { toast } from "sonner";

export const Route = createFileRoute("/_authenticated/workspaces/$workspaceId/paste")({
  component: PasteView,
});

const SEGMENTS = ["active_trial", "active_paid", "upgraded_paid", "churned_trial", "cancelled_paid", "second_tier_affiliate", "second_tier_commission"] as const;

function PasteView() {
  const { workspaceId } = useParams({ from: "/_authenticated/workspaces/$workspaceId/paste" });
  const interpret = useServerFn(aiInterpretPaste);
  const commit = useServerFn(commitManualPaste);
  const [text, setText] = useState("");
  const [segment, setSegment] = useState<string>("active_paid");
  const [parsing, setParsing] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [preview, setPreview] = useState<{ rows: any[]; detected_format: string } | null>(null);

  const onParse = async () => {
    setParsing(true);
    try {
      const r = await interpret({ data: { raw_text: text } });
      if (r.error || !r.result) toast.error(r.error ?? "Couldn't parse");
      else setPreview(r.result);
    } finally { setParsing(false); }
  };

  const onCommit = async () => {
    if (!preview) return;
    setCommitting(true);
    try {
      const res = await commit({ data: { workspace_id: workspaceId, segment_name: segment as any, rows: preview.rows, detected_format: preview.detected_format } });
      toast.success(`Added ${res.added} of ${res.total}`);
      setText(""); setPreview(null);
    } catch (e: any) {
      toast.error(e.message ?? "Failed");
    } finally { setCommitting(false); }
  };

  return (
    <div className="space-y-4 max-w-3xl">
      <Card>
        <CardContent className="p-4 space-y-3">
          <div>
            <Label>Paste rows</Label>
            <Textarea value={text} onChange={(e) => setText(e.target.value)} rows={10} className="font-mono text-xs mt-1" placeholder="Paste from your dashboard. Any format. We'll figure it out." />
          </div>
          <div className="flex items-center gap-2">
            <Label className="text-sm">Add to segment:</Label>
            <Select value={segment} onValueChange={setSegment}>
              <SelectTrigger className="w-56"><SelectValue /></SelectTrigger>
              <SelectContent>{SEGMENTS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
            <Button onClick={onParse} disabled={!text.trim() || parsing} className="ml-auto">
              {parsing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Sparkles className="h-4 w-4 mr-2" />}
              Parse
            </Button>
          </div>
        </CardContent>
      </Card>

      {preview && (
        <Card>
          <CardContent className="p-4 space-y-3">
            <div className="text-sm text-muted-foreground">Detected: {preview.detected_format} · {preview.rows.length} rows</div>
            <div className="rounded-lg border max-h-80 overflow-auto">
              <Table>
                <TableHeader><TableRow><TableHead>Email</TableHead><TableHead>First</TableHead><TableHead>Last</TableHead><TableHead>Phone</TableHead></TableRow></TableHeader>
                <TableBody>
                  {preview.rows.slice(0, 100).map((r, i) => (
                    <TableRow key={i}><TableCell className="font-mono text-xs">{r.email ?? ","}</TableCell><TableCell>{r.first_name ?? ","}</TableCell><TableCell>{r.last_name ?? ","}</TableCell><TableCell>{r.phone ?? ","}</TableCell></TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            <Button onClick={onCommit} disabled={committing}>
              {committing && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Add to {segment}
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
